import { styled } from 'styled-components';

import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import type { Color } from '#/theme/theme';
import { colors, fontWeights, transition } from '#/theme/theme';

import type { ButtonVariant } from './Button.types';
import { buttonAppearance, buttonSize, linkAppearance } from './buttonStyles';

// name of colors for icon initial color
const TEXT_COLORS: Record<ButtonVariant, Color> = {
  primary: 'white',
  secondary: 'brandBlue',
  'secondary-green': 'lightContentGrey',
  'secondary-white': 'white',
  danger: 'white',
  'danger-outline': 'alertRedHover',
  tertiary: 'brandBlue',
  'tertiary-grey': 'mediumContentGrey',
  'tertiary-white': 'brandBlue',
  'tertiary-danger': 'alertRedHover',
  link: 'brandBlue',
  'link-grey': 'mediumContentGrey',
  'link-white': 'white',
  'link-danger': 'alertRedHover',
  'glow-up': 'brandBlue',
};

export const StyledButtonOrLink = styled(ButtonOrLink)`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-family: inherit;
  cursor: pointer;
  transition: all ${transition};
  border: 1px solid transparent;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.full-width {
    width: 100%;
  }

  /* button variants: */
  &.primary {
    ${buttonAppearance};
    background-color: ${colors.brandBlue};
    border-color: ${colors.brandBlue};
    color: ${colors[TEXT_COLORS.primary]};

    &:hover,
    &:focus {
      background-color: ${colors.hoverBlue};
      border-color: ${colors.hoverBlue};
      text-decoration: none;
    }
  }

  &.secondary {
    ${buttonAppearance};
    border-color: ${colors.brandBlue};
    color: ${colors[TEXT_COLORS.secondary]};

    &:hover,
    &:focus {
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }

  &.secondary-white {
    ${buttonAppearance};
    border-color: ${colors.white};
    color: ${colors[TEXT_COLORS['secondary-white']]};

    &:hover,
    &:focus {
      color: ${colors.brandBlue};
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }

  &.secondary-green {
    ${buttonAppearance};
    border-color: ${colors.selectionGrey};
    color: ${colors[TEXT_COLORS['secondary-green']]};

    &:hover,
    &:focus {
      color: ${colors.white};
      background-color: ${colors.brandGreen};
      border-color: ${colors.brandGreen};
      text-decoration: none;
    }
  }

  &.danger {
    ${buttonAppearance};
    background-color: ${colors.alertRed};
    border-color: ${colors.alertRed};
    color: ${colors[TEXT_COLORS.danger]};

    &:hover,
    &:focus {
      background-color: ${colors.alertRedHover};
      border-color: ${colors.alertRedHover};
      text-decoration: none;
    }
  }

  &.danger-outline {
    ${buttonAppearance};
    border-color: ${colors.alertRed};
    color: ${colors[TEXT_COLORS['danger-outline']]};

    &:hover,
    &:focus {
      background-color: ${colors.alertRedHover};
      color: white;
      text-decoration: none;
    }
  }

  &.tertiary {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS.tertiary]};
    font-weight: ${fontWeights.semiBold};

    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration-line: underline;
      text-decoration-color: ${colors.hoverBlue};
    }
  }

  &.tertiary-grey {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS['tertiary-grey']]};
    font-weight: ${fontWeights.bold};

    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration-line: underline;
      text-decoration-color: ${colors[TEXT_COLORS['tertiary-grey']]};
    }
  }

  &.tertiary-white {
    ${linkAppearance};
    color: white;
    text-decoration-line: underline;

    &:hover {
      color: white;
      text-decoration-color: white;
    }
  }

  &.tertiary-danger {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS['tertiary-danger']]};
    font-weight: ${fontWeights.bold};

    &:hover,
    &:focus {
      text-decoration-line: underline;
      text-decoration-color: ${colors[TEXT_COLORS['tertiary-danger']]};
    }
  }

  &.link {
    /* block-no-empty */
    ${linkAppearance}
  }

  &.link-grey {
    ${linkAppearance};
    color: ${colors.mediumContentGrey};
  }

  &.link-danger {
    ${linkAppearance};
    color: ${colors.alertRedHover};
  }

  &.link-white {
    ${linkAppearance};
    color: white;
    text-decoration-line: underline;

    &:hover {
      color: white;
      text-decoration-color: white;
    }
  }

  /* nav link */
  &.active {
    color: ${colors.darkContentGrey};
    text-decoration: underline;
    font-weight: ${fontWeights.bold};
  }

  &.glow-up {
    ${buttonAppearance};

    background:
      linear-gradient(${colors.softBlue}, ${colors.softBlue}) padding-box,
      linear-gradient(
          270deg,
          ${colors.brandYellow},
          ${colors.brandGreen},
          ${colors.brandBlue}
        )
        border-box;
    border: 3px solid transparent;
  }

  /* button sizes: */
  &.tiny {
    /* block-no-empty */
    ${buttonSize({ $size: 'tiny' })};
  }

  &.small {
    /* block-no-empty */
    ${buttonSize({ $size: 'small' })};
  }

  &.medium {
    /* block-no-empty */
    ${buttonSize({ $size: 'medium' })};
  }

  &.large {
    /* block-no-empty */
    ${buttonSize({ $size: 'large' })};
  }

  &.tiny,
  &.small,
  &.medium,
  &.large {
    &.no-padding {
      padding: 0;
    }

    &.no-bold:not(.active) {
      font-weight: ${fontWeights.normal};
    }
  }

  &.rounded {
    border-radius: 1000rem;
  }
`;

export const buttonIconWrapperDistanceVar = '--button-icon-wrapper-gap';

// icons have a negative margin to compensate for padding internal to the SVG
export const ButtonIconWrapper = styled.div<{
  $position: 'left' | 'right';
}>`
  display: inline-flex;
  margin: -1px -2px;
  order: ${(props) => props.$position === 'right' && 100};
`;
